import React, { useState } from 'react';
import {
  IconButton,
  Menu,
  MenuItem,
  Avatar,
  ListItemIcon,
  ListItemText,
  Divider,
  Typography,
  Box,
} from '@mui/material';
import {
  Logout as LogoutIcon,
} from '@mui/icons-material';
import apiClient from './Auth/Api';

// Generate consistent color based on string (username)
const stringToColor = (string) => {
  if (!string) return '#1976d2';
  
  let hash = 0;
  for (let i = 0; i < string.length; i++) {
    hash = string.charCodeAt(i) + ((hash << 5) - hash);
  }
  
  // Generate vibrant, distinct colors
  const hue = hash % 360;
  const saturation = 65 + (hash % 20); // 65-85%
  const lightness = 45 + (hash % 15); // 45-60%
  
  return `hsl(${hue}, ${saturation}%, ${lightness}%)`;
};

// Get initials from user data (first letter of username)
const getInitials = (user) => {
  if (!user) return '?';
  
  // Priority: username > firstName > first_name > email
  if (user.username) {
    return user.username.charAt(0).toUpperCase();
  }
  if (user.firstName) {
    return user.firstName.charAt(0).toUpperCase();
  }
  if (user.first_name) {
    return user.first_name.charAt(0).toUpperCase();
  }
  if (user.email) {
    return user.email.charAt(0).toUpperCase();
  }
  
  return '?';
};

// Get display name
const getDisplayName = (user) => {
  if (!user) return 'User';
  
  // Try different name combinations
  if (user.firstName && user.lastName) {
    return `${user.firstName} ${user.lastName}`;
  }
  if (user.first_name && user.last_name) {
    return `${user.first_name} ${user.last_name}`;
  }
  if (user.firstName || user.first_name) {
    return user.firstName || user.first_name;
  }
  if (user.username) {
    return user.username;
  }
  
  return 'User';
};

// Get user email
const getUserEmail = (user) => {
  if (!user) return '';
  return user.email || '';
};

// Get user role display
const getUserRole = (user) => {
  if (!user || !user.role) return '';
  
  // Capitalize first letter of role
  return user.role.charAt(0).toUpperCase() + user.role.slice(1);
};

const ProfileMenu = () => {
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);
  
  // Get current user from API client
  const user = apiClient.getCurrentUser();
  
  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = async () => {
    handleClose();
    try {
      await apiClient.logout();
      // The logout callback should handle redirecting to login
      // If no callback is set, manually redirect
      if (!apiClient.logoutCallback) {
        window.location.href = '/management/login';
      }
    } catch (error) {
      console.error('Logout failed:', error);
      // Still proceed with logout even if API call fails
      window.location.href = '/management/login';
    }
  };

  const initials = getInitials(user);
  const displayName = getDisplayName(user);
  const userEmail = getUserEmail(user);
  const userRole = getUserRole(user);
  const avatarColor = user?.username ? stringToColor(user.username) : '#1976d2';

  return (
    <>
      <IconButton
        onClick={handleClick}
        size="small"
        sx={{ padding: 0.5 }}
      >
        <Avatar
          sx={{ 
            width: 32, 
            height: 32,
            bgcolor: avatarColor,
            fontWeight: 600,
            fontSize: '0.875rem'
          }}
        >
          {initials}
        </Avatar>
      </IconButton>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        onClick={handleClose}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        PaperProps={{
          sx: {
            width: 240,
            maxWidth: '100%',
            mt: 1.5,
          },
        }}
      >
        <Box sx={{ px: 2, py: 1.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
            <Avatar
              sx={{ 
                width: 40, 
                height: 40,
                bgcolor: avatarColor,
                fontWeight: 600,
                fontSize: '1rem',
                mr: 1.5
              }}
            >
              {initials}
            </Avatar>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography 
                variant="subtitle1" 
                sx={{ 
                  fontWeight: 600,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap'
                }}
              >
                {displayName}
              </Typography>
              {userRole && (
                <Typography 
                  variant="caption" 
                  sx={{ 
                    color: 'primary.main',
                    fontWeight: 500
                  }}
                >
                  {userRole}
                </Typography>
              )}
            </Box>
          </Box>
          {userEmail && (
            <Typography 
              variant="body2" 
              color="text.secondary"
              sx={{
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }}
            >
              {userEmail}
            </Typography>
          )}
        </Box>

        <Divider />

        <MenuItem 
          onClick={handleLogout} 
          sx={{ 
            color: 'error.main',
            py: 1.5,
            '&:hover': {
              backgroundColor: 'error.lighter'
            }
          }}
        >
          <ListItemIcon>
            <LogoutIcon fontSize="small" color="error" />
          </ListItemIcon>
          <ListItemText>Logout</ListItemText>
        </MenuItem>
      </Menu>
    </>
  );
};

export default ProfileMenu;